import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer, switchMap, catchError, of, filter, take } from 'rxjs';

interface VersionInfo {
  version: string;
  buildTime: string;
}

/**
 * Polls /version.json every 5 minutes to detect new deployments.
 * When a version mismatch is found, shows a persistent snackbar prompting the user to reload.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private ngZone = inject(NgZone);

  private currentVersion: string | null = null;
  private pollSub: Subscription | null = null;

  /** Interval in ms between version checks (5 minutes) */
  private readonly POLL_INTERVAL = 5 * 60 * 1000;

  /** Start polling for new versions. Call once from AppComponent. */
  startPolling(): void {
    // Run outside Angular zone to avoid triggering change detection on every tick
    this.ngZone.runOutsideAngular(() => {
      this.pollSub = timer(0, this.POLL_INTERVAL)
        .pipe(
          switchMap(() =>
            this.http.get<VersionInfo>(`/version.json?t=${Date.now()}`).pipe(
              catchError(() => of(null))
            )
          ),
          filter((info): info is VersionInfo => !!info)
        )
        .subscribe((info) => {
          if (!this.currentVersion) {
            // First check — store current version
            this.currentVersion = info.version;
            return;
          }

          if (info.version !== this.currentVersion) {
            this.ngZone.run(() => this.showUpdateNotification());
          }
        });
    });
  }

  private showUpdateNotification(): void {
    const message = this.translate.instant('AppUpdate.NewVersionAvailable');
    const action = this.translate.instant('AppUpdate.Reload');

    const ref = this.snackBar.open(message, action, {
      duration: 0, // persistent until user acts
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: 'app-update-snackbar',
    });

    ref.onAction().pipe(take(1)).subscribe(() => {
      window.location.reload();
    });
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }
}
